import { MoneyFormatter } from '@hooks/useFormatters';
import { CompanyClient } from './clients';
import { ProductType } from './products';

export enum DiscountType {
  Percent = 'percent',
  Amount = 'amount',
  DeductibleAmount = 'deductibleAmount',
}

export const DiscountTypeDefault = DiscountType.Percent;

export enum PromoCodeType {
  SingleUse = 'singleUse',
  OnePerUser = 'onePerUser',
  CountLimit = 'countLimit',
  Unlimited = 'unlimited',
}

export const PromoCodeTypeDefault = PromoCodeType.SingleUse;

export interface PromoCodeUsage {
  id: string;
  reservationId?: string;
  slotId?: string;
  deepLink: string;
  client: CompanyClient;

  requestedAt?: string;
  receivedAt?: string;
}

export interface PromoCode {
  id: string;
  code: string;
  type: PromoCodeType;
  comment?: string;
  useLimit: number;
  useCount?: number;
  spotsPerUseLimit: number;

  calendarId?: string;
  calendarTitle?: string;

  serviceId?: string;
  serviceTitle?: string;

  client?: CompanyClient;

  currency: string;
  discount: number;
  discountType: DiscountType;
  activeFrom: string;
  activeTill: string;
  reservationsFrom?: string;
  reservationsTill?: string;

  uses?: PromoCodeUsage[];

  productType: ProductType;
  productId?: string;
  productTitle?: string;
  productSubscriptionId?: string;

  reservationToken?: string;
  customForm?: string;
  giftCardId?: string;
}

export interface CreatePromoCodeRequest {
  type: PromoCodeType;
  discount: number;
  discountType: DiscountType;
  useLimit: number;
  spotsPerUseLimit: number;
  comment: string;
  activeFrom: string;
  activeTill: string;
  reservationsFrom?: string;
  reservationsTill?: string;
  calendarId?: string;
  serviceId?: string;
  clientId?: string;
}

export const formatPromoCodeDiscount = (
  promoCode: Pick<PromoCode, 'discount' | 'discountType' | 'currency'>,
  moneyFormatter: MoneyFormatter
): string => {
  if (
    promoCode.discountType === DiscountType.Amount ||
    promoCode.discountType === DiscountType.DeductibleAmount
  ) {
    return moneyFormatter.format(promoCode.discount, {
      currency: promoCode.currency,
      fromCents: true,
    });
  } else {
    return `${promoCode.discount}%`;
  }
};

export const formatPromoCodeType = (promoCodeType: PromoCodeType): string => {
  switch (promoCodeType) {
    case PromoCodeType.SingleUse: {
      return 'promo_codes_type_single_use';
    }
    case PromoCodeType.OnePerUser: {
      return 'promo_codes_type_once_per_customer';
    }
    case PromoCodeType.CountLimit: {
      return 'promo_codes_type_use_count_limit';
    }
    case PromoCodeType.Unlimited: {
      return 'promo_codes_type_unlimited';
    }
    default: {
      return '';
    }
  }
};
